import React from 'react';
import { useNavigate } from 'react-router-dom';

import {
  useProductGroupCreate,
  useFuelProductGroupCreate,
} from '@occc-app/dataAccessLayer/productGroup/mutations';
import { Product_Group_Key, ProductGroupCreateMutation } from '@occc/graphql';
import { ConfirmDialog, useConfirmDialog, Typography } from '@occc/ui-design-system';
import { useChainId } from '@occc-app/Chain/hooks';

import { ProductGroupForm } from '../components/ProductGroupForm';
import {
  OnCreateProductGroupCompleted,
  ProductGroupFormRenderCallback,
  ProductGroupFormValues,
} from '../types';
import { routeCreator, parseSortingRank } from '../utils';

export const createProductGroupFormId = 'createProductGroupForm';

const initialValues = {
  name: '',
  description: '',
  key: Product_Group_Key.Upc,
  values: [],
};

interface ICreateProductGroupFormContainerProps {
  renderFormChildren: ProductGroupFormRenderCallback;
  onCreateCompleted?: OnCreateProductGroupCompleted;
  refetchQueries?: string[];
}

export const CreateProductGroupFormContainer: React.FC<ICreateProductGroupFormContainerProps> = ({
  renderFormChildren,
  onCreateCompleted,
  refetchQueries,
}) => {
  const navigate = useNavigate();
  const chainId = useChainId();
  const [productGroupValues, setProductGroupValues] = React.useState<ProductGroupFormValues | null>(
    null,
  );

  const onCompleted = (mutation: ProductGroupCreateMutation) => {
    if (!onCreateCompleted) {
      navigate(routeCreator.details(mutation.productGroupCreate.id));
    } else {
      onCreateCompleted(mutation.productGroupCreate);
    }
  };
  const [productGroupCreateMutation] = useProductGroupCreate({
    onCompleted,
    refetchQueries: refetchQueries || [],
  });
  const [fuelProductGroupCreateMutation] = useFuelProductGroupCreate({
    onCompleted: (mutation) => {
      if (onCreateCompleted) {
        onCreateCompleted(mutation.fuelProductGroupCreate);
      } else if (mutation.fuelProductGroupCreate) {
        navigate(routeCreator.details(mutation.fuelProductGroupCreate.id));
      }
    },
    refetchQueries: refetchQueries || [],
  });

  const onCreateDefaultTARProductGroup = () => {
    if (productGroupValues) {
      createProductGroup(productGroupValues);
      setProductGroupValues(null);
    }
  };

  const { open, setOpen, onCloseButtonClick, onConfirmButtonClick } = useConfirmDialog({
    onConfirm: onCreateDefaultTARProductGroup,
    shouldStopEventPropagation: true,
  });

  const onSubmit = (input: ProductGroupFormValues) => {
    if (input.default && input.userSelectable) {
      setProductGroupValues(input);
      setOpen(true);
      return;
    }

    createProductGroup(input);
  };

  const createProductGroup = (input: ProductGroupFormValues) => {
    const { userSelectable, sortingRank, default: isDefault, ...productGroupInput } = input;
    if (productGroupInput.key === Product_Group_Key.FuelGradeId) {
      fuelProductGroupCreateMutation({
        variables: {
          chainId,
          input: {
            userSelectable,
            sortingRank: parseSortingRank(sortingRank),
            default: userSelectable ? isDefault : false,
            productGroup: productGroupInput,
          },
        },
      });
      return;
    }

    productGroupCreateMutation({
      variables: {
        chainId,
        input: productGroupInput,
      },
    });
  };

  return (
    <>
      <ProductGroupForm
        id={createProductGroupFormId}
        readOnly={false}
        initialValues={initialValues}
        onSubmit={onSubmit}
        title='Create Product Group'
      >
        {renderFormChildren}
      </ProductGroupForm>
      {open && (
        <ConfirmDialog
          dialogTitle='Default for Total Available Rollback'
          open={open}
          onClose={onCloseButtonClick}
          confirmButtonText='Confirm'
          confirmButtonProps={{
            onClick: onConfirmButtonClick,
          }}
        >
          <Typography variant='body2'>
            Are you sure you would like to set this product group as the default for all members
            when calculating Total Available Rollback? This will override your previous default.
          </Typography>
        </ConfirmDialog>
      )}
    </>
  );
};

export default CreateProductGroupFormContainer;
